import { useState, useEffect, useMemo } from "react"
import { useToast } from "@crm-src/hooks/use-toast"
import { licenceSectionOf, type PriceListCategory } from "@xop/dashboard-shared"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material"

import { ResellerSelector } from "@crm-src/components/reseller-selector"
import { ResellerOnly } from "@crm-src/components/reseller"
import { FeatureTilePicker, type FeatureTileItem } from "@crm-src/components/feature-tiles"
import { MultiSelect } from "@/components/ui/multi-select"
import { customersApi } from "@crm-src/lib/customers-api"
import { dealsApi } from "@crm-src/lib/deals-api"
import { dealersApiService } from "@crm-src/lib/dealers-api"
import { productsApi, type Product, type ProductFeature } from "@crm-src/lib/products-api"
// Releases live in the support-center (single timeline), surfaced via
// the dashboard's documentation proxy — the SAME working client the
// System Admin ▸ Releases panel uses. The old @crm-src/lib/releases-api
// hit /api/crm/releases, which the dashboard never proxies → the
// Agreement Release dropdown was always empty.
import { listReleases, type ReleaseDTO } from "@crm-src/pages/releases/releases-api"
import type { DetailedCustomer, CustomerUpdateParams } from "@crm-src/types/customer"

/**
 * The features an AGREEMENT can name (2026-09-15): the customer's product's
 * Value Added Services and Platform Extensions, from CRM ▸ Pricing, under the
 * same two headings the licence builder and the appliance use. Two categories
 * of the price book are deliberately NOT offered here — a support plan is the
 * Support level below, and a demo kit is a whole system, not a feature of one.
 */
const AGREEMENT_EXCLUDED_CATEGORIES: ReadonlySet<PriceListCategory> = new Set<PriceListCategory>(["support", "demo_system"])

/** One tile per Pricing feature of the product. `id` is the 4-digit CODE the
 * agreement stores — every feature has one now; a row without one (an older
 * crm-api) is shown but cannot be picked, because nothing could be saved. The
 * code is never printed: the part number is the caption. */
export function agreementFeatureItems(features: readonly ProductFeature[], selectedCodes: readonly string[]): FeatureTileItem[] {
  const selected = new Set(selectedCodes)
  return features
    .filter((feature) => !AGREEMENT_EXCLUDED_CATEGORIES.has(feature.category))
    .map((feature) => {
      const code = feature.code ?? ""
      return {
        id: code || `uncoded:${feature.id}`,
        name: feature.name,
        section: licenceSectionOf(feature.category),
        caption: code ? (feature.part_number ?? "") : "No code yet — set one in CRM ▸ Pricing.",
        on: Boolean(code) && selected.has(code),
        selectable: Boolean(code),
        retired: feature.active === false,
      }
    })
}

interface EditAgreementModalProps {
  isOpen: boolean
  onClose: () => void
  customer: DetailedCustomer | null
  onAgreementUpdated: (updatedCustomer: DetailedCustomer) => void
}

type SupportLevel = "none" | "weekdays" | "24_7"

// Mirror of xop-brain `app/models/support_plan.rb` (SupportPlan::CATALOG).
// Used only to show the *derived* plan name + SLA as a live placeholder
// as the operator toggles the support level — the authoritative
// resolution still happens server-side. Keep these three rows in
// lock-step with the Ruby catalog (kept tiny on purpose; not worth a
// shared package for three entries).
const SUPPORT_PLAN_CATALOG: Record<SupportLevel, {
  name: string
  firstResponseMinutes: number | null
  resolutionMinutes: number | null
}> = {
  none:     { name: "No Support Plan",        firstResponseMinutes: null, resolutionMinutes: null },
  weekdays: { name: "Standard (Mon–Fri 9×5)", firstResponseMinutes: 480,  resolutionMinutes: 2880 },
  "24_7":   { name: "24/7 Premium",           firstResponseMinutes: 60,   resolutionMinutes: 480 },
}

// The CRM CustomerSerializer emits deal dates as "MM/DD/YYYY"; an
// <input type="date"> needs "YYYY-MM-DD". Bridge the two without a dep.
function toDateInput(value?: string | null): string {
  if (!value) return ""
  const mdy = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value)
  if (mdy) return `${mdy[3]}-${mdy[1]}-${mdy[2]}`
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(value)
  return iso ? iso[1] : ""
}

interface AgreementData {
  reseller_id?: string
  dealer_ids: string[]
  support_level: SupportLevel
  // Product + the release the customer runs (the old single
  // feature/version cascade is replaced by the multi-feature catalog
  // below + an explicit release pick). product_id stays on the
  // customer record; installed_release_version drives the portal
  // Version panel.
  product_id?: string
  installed_release_version: string
  equipment_price: string // Keep as string to preserve decimal input
  maintenance_percentage: string // Keep as string to preserve decimal input
  // SLA — optional overrides on top of the support_level derived
  // defaults. Blank ⇒ the server derives from the SupportPlan catalog.
  // The plan NAME is always derived from support_level (no override).
  sla_first_response_minutes: string
  sla_resolution_minutes: string
  // Licence term lives on the primary deal; the customer-support
  // portal renders these as Started / Ends.
  license_start_date: string
  license_end_date: string
  // Purchased features of the customer's PRODUCT — stored as the 4-digit
  // ProductFeature codes. Synced to the portal, which flips each Feature card
  // Active/Inactive by code. (Per-product model — no global slug catalog.)
  feature_codes: string[]
  // Ports the customer PURCHASED (capacity). This is an agreement
  // term — "used" is runtime consumption and is NOT edited here.
  // Synced to the customer-support portal licence panel as the
  // audio/web port totals (snapshot license.ports.*.total).
  audio_ports_total: string
  web_ports_total: string
  // How many servers the customer runs. Operator-recorded inventory,
  // NOT runtime telemetry — nothing discovers it, someone types it.
  server_count: string
}

export function EditAgreementModal({
  isOpen,
  onClose,
  customer,
  onAgreementUpdated
}: EditAgreementModalProps) {
  const { toast } = useToast()
  const [isUpdating, setIsUpdating] = useState(false)
  const [availableDealers, setAvailableDealers] = useState<{label: string, value: string}[]>([])

  const [isDealersLoading, setIsDealersLoading] = useState(false)
  const [products, setProducts] = useState<Product[]>([])
  // The per-product feature catalog (productId → its features, each with a
  // 4-digit code). The customer picks from THEIR product's features.
  const [productFeatures, setProductFeatures] = useState<{ [productId: number]: ProductFeature[] }>({})
  const [releases, setReleases] = useState<ReleaseDTO[]>([])

  // Agreement form data
  const [agreementData, setAgreementData] = useState<AgreementData>({
    reseller_id: undefined,
    dealer_ids: [],
    support_level: "none",
    product_id: undefined,
    installed_release_version: "",
    equipment_price: "",
    maintenance_percentage: "",
    sla_first_response_minutes: "",
    sla_resolution_minutes: "",
    license_start_date: "",
    license_end_date: "",
    feature_codes: [],
    audio_ports_total: "",
    web_ports_total: "",
    server_count: "",
  })

  // Populate form data when customer changes
  useEffect(() => {
    if (customer && isOpen) {
      // Get data from the primary deal
      const primaryDeal = customer.customer_deals?.[0]

      setAgreementData({
        reseller_id: customer.reseller?.id ? String(customer.reseller.id) : undefined,
        dealer_ids: customer.dealers?.map(d => String(d.id)) || [],
        support_level: customer.support_level || "none",
        product_id: customer.product_id ? String(customer.product_id) : undefined,
        installed_release_version: customer.installed_release_version ?? "",
        equipment_price: (primaryDeal?.equipment_price || primaryDeal?.amount || 0) === 0 ? "" : String(primaryDeal?.equipment_price || primaryDeal?.amount || 0),
        maintenance_percentage: (primaryDeal?.maintenance_percentage || 0) === 0 ? "" : String(primaryDeal?.maintenance_percentage || 0),
        // SLA raw overrides only — blank stays blank = "derive from
        // support level". Plan name is always derived (no override).
        sla_first_response_minutes: customer.sla_first_response_minutes != null ? String(customer.sla_first_response_minutes) : "",
        sla_resolution_minutes: customer.sla_resolution_minutes != null ? String(customer.sla_resolution_minutes) : "",
        license_start_date: toDateInput(primaryDeal?.start_date),
        license_end_date: toDateInput(primaryDeal?.end_date),
        feature_codes: Array.isArray(customer.feature_codes) ? customer.feature_codes : [],
        audio_ports_total: customer.audio_ports_total != null ? String(customer.audio_ports_total) : "",
        web_ports_total:   customer.web_ports_total   != null ? String(customer.web_ports_total)   : "",
        server_count:      customer.server_count      != null ? String(customer.server_count)      : "",
      })
    }
  }, [customer, isOpen])

  // Load the product list (Product picker) + the per-product feature catalog
  // (each product's own features, with codes) once the modal opens.
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    productsApi
      .listProducts()
      .then((ps) => { if (!cancelled) setProducts(ps) })
      .catch((error) => { console.error("Failed to load products", error) })
    productsApi
      .getProductHierarchy()
      .then((h) => { if (!cancelled) setProductFeatures(h.productFeatures || {}) })
      .catch((error) => { console.error("Failed to load product features", error) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  // Releases for the Release picker — scoped to the selected product
  // when one is set, otherwise the full timeline.
  //
  // BUILDS ONLY (2026-09-12). This field records the version a customer is
  // RUNNING, and it is joined to portal-api by exact string match
  // (`Sync::ApplySnapshot#upsert_installed_release`). A release LINE (10.2) is
  // a family name — no appliance reports it, and storing one would silently
  // resolve to nothing on the portal side.
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    listReleases()
      .then((rs) => { if (!cancelled) setReleases(rs.filter((r) => r.parent_release_id !== null)) })
      .catch((error) => { console.error("Failed to load releases", error); if (!cancelled) setReleases([]) })
    return () => { cancelled = true }
  }, [isOpen, agreementData.product_id])

  const toggleFeature = (code: string) => {
    setAgreementData(prev => ({
      ...prev,
      feature_codes: prev.feature_codes.includes(code)
        ? prev.feature_codes.filter(c => c !== code)
        : [...prev.feature_codes, code],
    }))
  }

  const selectedProductId = agreementData.product_id ? parseInt(agreementData.product_id, 10) : undefined
  const featureItems = useMemo(
    () => (selectedProductId != null ? agreementFeatureItems(productFeatures[selectedProductId] ?? [], agreementData.feature_codes) : []),
    [selectedProductId, productFeatures, agreementData.feature_codes],
  )


  // Fetch dealers when reseller changes
  useEffect(() => {
    const fetchDealers = async () => {
      if (agreementData.reseller_id) {
        setIsDealersLoading(true)
        try {
          const dealers = await dealersApiService.getByReseller(agreementData.reseller_id)
          setAvailableDealers(dealers.map(d => ({ label: d.name, value: String(d.id) })))
        } catch (error) {
          console.error("Failed to fetch dealers", error)
          setAvailableDealers([])
        } finally {
          setIsDealersLoading(false)
        }
      } else {
        setAvailableDealers([])
        // Clear selected dealers if reseller is cleared or changed (optional, but safer)
        // Usually if reseller changes, dealers are invalid.
        // But here we are in useEffect which runs on reseller_id change.
        // We should probably clear dealer_ids if the new reseller doesn't have the old dealers.
        // But for now, let's just update available dealers.
        // Ideally, if user changes reseller manually, we should clear dealer_ids.
        // But if it's initial load, we shouldn't.
        // The initialization useEffect handles initial load.
        // This useEffect handles updates.
        // We can add a check if it's user interaction vs initial load, but simpler is:
        // If reseller changes, clear dealers?
        // Or just let user clear them.
        // Let's keep it simple.
      }
    }
    fetchDealers()
  }, [agreementData.reseller_id])

  const derivedPlan = SUPPORT_PLAN_CATALOG[agreementData.support_level]

  // Handle agreement update
  const handleUpdateAgreement = async () => {
    if (!customer) return

    try {
      setIsUpdating(true)

      // Update customer data (reseller, dealers, support level, product /
      // feature / version, plan name + SLA overrides).
      const toId = (raw?: string): number | undefined =>
        raw && raw !== 'none' ? parseInt(raw, 10) : undefined

      const toMinutes = (raw: string): number | null => {
        const trimmed = raw.trim()
        if (trimmed === "") return null
        const n = parseInt(trimmed, 10)
        return Number.isFinite(n) && n >= 0 ? n : null
      }

      // The ports AND server_count columns are NOT NULL default 0 on the
      // CRM customer, so a blank/garbage input is a safe 0 (not null).
      const toPorts = (raw: string): number => {
        const n = parseInt(raw.trim(), 10)
        return Number.isFinite(n) && n >= 0 ? n : 0
      }

      const customerUpdateData: CustomerUpdateParams = {
        reseller_id: agreementData.reseller_id,
        dealer_ids: agreementData.dealer_ids,
        support_level: agreementData.support_level,
        product_id: toId(agreementData.product_id),
        // Which release the customer runs → portal Version panel.
        installed_release_version: agreementData.installed_release_version || null,
        // Plan name is always derived from support_level server-side
        // (SupportPlan catalog). Blank SLA ⇒ the catalog default.
        sla_first_response_minutes: toMinutes(agreementData.sla_first_response_minutes),
        sla_resolution_minutes: toMinutes(agreementData.sla_resolution_minutes),
        feature_codes: agreementData.feature_codes,
        audio_ports_total: toPorts(agreementData.audio_ports_total),
        web_ports_total:   toPorts(agreementData.web_ports_total),
        server_count:      toPorts(agreementData.server_count),
      }

      const customerResponse = await customersApi.update(customer.id, customerUpdateData)

      // Update deal data (equipment / maintenance + licence term dates).
      const primaryDeal = customer.customer_deals?.[0]
      if (primaryDeal) {
        const dealUpdateData = {
          equipment_price: parseFloat(agreementData.equipment_price) || 0,
          maintenance_percentage: parseFloat(agreementData.maintenance_percentage) || 0,
          start_date: agreementData.license_start_date || undefined,
          end_date: agreementData.license_end_date || undefined,
        }

        await dealsApi.updateDeal(primaryDeal.id, dealUpdateData)
      } else {
        console.warn('No primary deal found for customer', customer.id)
        toast({
          title: "Warning",
          description: "Customer info updated, but no deal found to update pricing / licence term",
          variant: "destructive",
        })
      }

      onAgreementUpdated(customerResponse.customer as DetailedCustomer)
      onClose()

      toast({
        title: "Success",
        description: "Agreement updated successfully",
      })
    } catch (error) {
      console.error('Error updating agreement:', error)
      toast({
        title: "Error",
        description: "Failed to update agreement",
        variant: "destructive",
      })
    } finally {
      setIsUpdating(false)
    }
  }

  const handleClose = () => {
    if (!isUpdating) {
      onClose()
    }
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{ sx: { width: "fit-content", minWidth: 700, maxWidth: "90vw", maxHeight: "90vh" } }}
    >
      <DialogTitle>Edit Agreement</DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ mb: 2 }}>
          Update agreement details: reseller, product, support plan, SLA,
          licence term and financial terms.
        </DialogContentText>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Product, Features & Release — one section, synced to the
                customer portal (licence + features + version panels). */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Typography component="h4" sx={{ fontWeight: 500, fontSize: 14, color: "text.primary" }}>Product, Features &amp; Release</Typography>
              <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", mt: -1 }}>
                Product, the purchased features, and the release the customer
                runs. Features and prices are managed in CRM ▸ Pricing, releases
                in CRM ▸ Releases. All of this syncs to the customer portal.
              </Typography>

              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <TextField
                    select
                    label="Product"
                    id="edit_product"
                    size="small"
                    fullWidth
                    value={agreementData.product_id ?? "none"}
                    onChange={(e) =>
                      setAgreementData(prev => ({
                        ...prev,
                        product_id: e.target.value === "none" ? undefined : e.target.value,
                        // release list is product-scoped — reset the pick
                        installed_release_version: "",
                      }))
                    }
                  >
                    <MenuItem value="none">None</MenuItem>
                    {products.map((p) => (
                      <MenuItem key={p.id} value={String(p.id)}>
                        {p.name}{p.code ? ` (${p.code})` : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <TextField
                    select
                    label="Release"
                    id="edit_release"
                    size="small"
                    fullWidth
                    value={agreementData.installed_release_version || "none"}
                    onChange={(e) =>
                      setAgreementData(prev => ({
                        ...prev,
                        installed_release_version: e.target.value === "none" ? "" : e.target.value,
                      }))
                    }
                  >
                    <MenuItem value="none">None</MenuItem>
                    {releases.map((r) => (
                      <MenuItem key={r.id} value={r.version}>
                        v{r.version}{r.released ? "" : ` · ${r.status}`}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>

              {/* Ports the customer PURCHASED (capacity). Agreement
                  term only — runtime "used" is not edited here. Syncs
                  to the customer-support portal licence panel
                  (snapshot license.ports.{audio,web}.total). */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <FormLabel>Ports purchased</FormLabel>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <FormLabel htmlFor="edit_audio_ports_total" sx={{ fontSize: 12, color: "text.secondary" }}>Audio ports</FormLabel>
                    <TextField
                      id="edit_audio_ports_total"
                      type="number"
                      size="small"
                      fullWidth
                      inputProps={{ min: 0 }}
                      value={agreementData.audio_ports_total}
                      onChange={(e) => setAgreementData(prev => ({ ...prev, audio_ports_total: e.target.value }))}
                      placeholder="0"
                    />
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <FormLabel htmlFor="edit_web_ports_total" sx={{ fontSize: 12, color: "text.secondary" }}>Web ports</FormLabel>
                    <TextField
                      id="edit_web_ports_total"
                      type="number"
                      size="small"
                      fullWidth
                      inputProps={{ min: 0 }}
                      value={agreementData.web_ports_total}
                      onChange={(e) => setAgreementData(prev => ({ ...prev, web_ports_total: e.target.value }))}
                      placeholder="0"
                    />
                  </Box>
                </Box>
              </Box>

              {/* Servers the customer RUNS. Operator-recorded agreement
                  inventory — nothing discovers it and nothing bills off
                  it; it answers "how many servers does this customer
                  have" on the Agreement card. Same NOT NULL default-0
                  column shape as the ports above. */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <FormLabel htmlFor="edit_server_count">Servers</FormLabel>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                  <TextField
                    id="edit_server_count"
                    type="number"
                    size="small"
                    fullWidth
                    inputProps={{ min: 0 }}
                    value={agreementData.server_count}
                    onChange={(e) => setAgreementData(prev => ({ ...prev, server_count: e.target.value }))}
                    placeholder="0"
                  />
                </Box>
              </Box>

              {/* Features — the customer's PRODUCT's own features from CRM ▸
                  Pricing, under the two headings every other surface uses
                  (2026-09-15). Ticking one stores its 4-digit code; the code
                  itself is never printed here. */}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }} data-testid="agreement-features">
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <FormLabel>Features</FormLabel>
                  <Typography component="span" sx={{ fontSize: 12, color: "text.secondary" }}>
                    {agreementData.feature_codes.length} selected
                  </Typography>
                </Box>
                {selectedProductId == null ? (
                  <Box sx={{ px: 1.5, py: 1, border: 1, borderColor: "divider", borderRadius: 1, fontSize: 14, color: "text.secondary", bgcolor: "surfaceSoft" }}>
                    Select a product above to choose its features.
                  </Box>
                ) : featureItems.length === 0 ? (
                  <Box sx={{ px: 1.5, py: 1, border: 1, borderColor: "divider", borderRadius: 1, fontSize: 14, color: "text.secondary", bgcolor: "surfaceSoft" }}>
                    This product has no features yet — add them in CRM ▸ Pricing.
                  </Box>
                ) : (
                  <FeatureTilePicker
                    items={featureItems}
                    disabled={isUpdating}
                    onToggle={toggleFeature}
                    groupTestIdPrefix="agreement-feature-group"
                  />
                )}
              </Box>
            </Box>

            {/* Agreement Details */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }} />
              {/* Reseller wall: the reseller picker AND its dependent dealers
                  selector drop together for a viewer without crm:resellers, so
                  the 1fr/1fr grid never shows a lone empty column. See
                  components/reseller.tsx. */}
              <ResellerOnly>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <ResellerSelector
                    value={agreementData.reseller_id}
                    onValueChange={(value) => {
                      // When reseller changes, clear selected dealers as they belong to previous reseller
                      setAgreementData({
                        ...agreementData,
                        reseller_id: value ?? undefined,
                        dealer_ids: []
                      })
                    }}
                    label="Reseller"
                    placeholder="Select a reseller..."
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FormLabel>Dealers</FormLabel>
                  {isDealersLoading ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: 40, px: 1.5, py: 1, border: 1, borderColor: "divider", borderRadius: 1, fontSize: 14, color: "text.secondary", bgcolor: "surfaceSoft" }}>
                      <CircularProgress size={16} color="inherit" />
                      <Box component="span">Loading dealers...</Box>
                    </Box>
                  ) : agreementData.reseller_id && availableDealers.length === 0 ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: 40, px: 1.5, py: 1, border: 1, borderColor: "divider", borderRadius: 1, fontSize: 14, color: "text.secondary", bgcolor: "surfaceSoft", cursor: "not-allowed" }}>
                      <Box component="span">No dealers available for this reseller</Box>
                    </Box>
                  ) : (
                    <Box {...(!agreementData.reseller_id ? { sx: { opacity: 0.5, cursor: "not-allowed" } } : {})}>
                      <MultiSelect
                        options={availableDealers}
                        selected={agreementData.dealer_ids}
                        onChange={(selected) => setAgreementData({ ...agreementData, dealer_ids: selected })}
                        placeholder={agreementData.reseller_id ? "Select dealers..." : "Select reseller first"}
                      />
                    </Box>
                  )}
                </Box>
              </Box>
              </ResellerOnly>
            </Box>

            {/* Support Plan & SLA */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }} />
              <Typography component="h4" sx={{ fontWeight: 500, fontSize: 14, color: "text.primary" }}>Support Plan &amp; SLA</Typography>
              <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", mt: -1 }}>
                The plan name is derived from the support level and shown on
                the customer portal. Leave the SLA fields blank to use the
                defaults for the selected level — these sync to the portal
                and set the deadline on every new support ticket.
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <TextField
                    select
                    label="Support Level"
                    id="edit_support_level"
                    size="small"
                    fullWidth
                    value={agreementData.support_level}
                    onChange={(e) => setAgreementData({ ...agreementData, support_level: e.target.value as SupportLevel })}
                  >
                    <MenuItem value="none">NONE</MenuItem>
                    <MenuItem value="weekdays">WEEK DAYS</MenuItem>
                    <MenuItem value="24_7">24/7</MenuItem>
                  </TextField>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FormLabel htmlFor="edit_sla_first_response">First response (min)</FormLabel>
                  <TextField
                    id="edit_sla_first_response"
                    type="number"
                    size="small"
                    fullWidth
                    inputProps={{ min: "0", step: "1" }}
                    value={agreementData.sla_first_response_minutes}
                    onChange={(e) => setAgreementData({ ...agreementData, sla_first_response_minutes: e.target.value })}
                    placeholder={derivedPlan.firstResponseMinutes != null ? String(derivedPlan.firstResponseMinutes) : "—"}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FormLabel htmlFor="edit_sla_resolution">Resolution (min)</FormLabel>
                  <TextField
                    id="edit_sla_resolution"
                    type="number"
                    size="small"
                    fullWidth
                    inputProps={{ min: "0", step: "1" }}
                    value={agreementData.sla_resolution_minutes}
                    onChange={(e) => setAgreementData({ ...agreementData, sla_resolution_minutes: e.target.value })}
                    placeholder={derivedPlan.resolutionMinutes != null ? String(derivedPlan.resolutionMinutes) : "—"}
                  />
                </Box>
              </Box>
            </Box>

            {/* Licence Term */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }} />
              <Typography component="h4" sx={{ fontWeight: 500, fontSize: 14, color: "text.primary" }}>Licence Term</Typography>
              <Typography component="p" sx={{ fontSize: 12, color: "text.secondary", mt: -1 }}>
                Start / end drive the "Started" and "Ends" dates on the
                customer's licence panel.
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FormLabel htmlFor="edit_license_start">Start date</FormLabel>
                  <TextField
                    id="edit_license_start"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={agreementData.license_start_date}
                    onChange={(e) => setAgreementData({ ...agreementData, license_start_date: e.target.value })}
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FormLabel htmlFor="edit_license_end">End date</FormLabel>
                  <TextField
                    id="edit_license_end"
                    type="date"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={agreementData.license_end_date}
                    onChange={(e) => setAgreementData({ ...agreementData, license_end_date: e.target.value })}
                  />
                </Box>
              </Box>
            </Box>

            {/* Financial Terms */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }} />
              <Typography component="h4" sx={{ fontWeight: 500, fontSize: 14, color: "text.primary" }}>Financial Terms</Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FormLabel htmlFor="edit_equipment_price">Equipment Price</FormLabel>
                  <TextField
                    id="edit_equipment_price"
                    type="number"
                    size="small"
                    fullWidth
                    inputProps={{ min: "0", step: "0.01" }}
                    value={agreementData.equipment_price}
                    onChange={(e) => {
                      setAgreementData({
                        ...agreementData,
                        equipment_price: e.target.value
                      })
                    }}
                    placeholder="0.00"
                  />
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <FormLabel htmlFor="edit_maintenance_percentage">Maintenance (%)</FormLabel>
                  <TextField
                    id="edit_maintenance_percentage"
                    type="number"
                    size="small"
                    fullWidth
                    inputProps={{ min: "0", max: "100", step: "0.1" }}
                    value={agreementData.maintenance_percentage}
                    onChange={(e) => {
                      setAgreementData({
                        ...agreementData,
                        maintenance_percentage: e.target.value
                      })
                    }}
                    placeholder="0.0"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleClose} disabled={isUpdating}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleUpdateAgreement} disabled={isUpdating} {...(isUpdating ? { startIcon: <CircularProgress size={16} color="inherit" /> } : {})}>
          {isUpdating ? "Updating..." : "Update Agreement"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
